/**
 * Imperative helpers for editing the Release Train document inside its Shadow
 * DOM: parse/apply matrix bars, add/remove calendar items and bars, inject the
 * edit-mode "chrome" (completion checkboxes, add/remove buttons), and serialize
 * back to clean HTML with all chrome stripped.
 *
 * State that must survive a save is stored on the elements themselves —
 * `data-done` on a calendar <li>, `data-hidden` on a bar, colour/ms via class,
 * start/end via the bar's grid-column — so it round-trips through the stored
 * HTML. Checkboxes and buttons are transient chrome and never persisted.
 */
export const BAR_COLOURS = ["navy", "blue", "amber", "green", "grey"] as const
export type BarColour = (typeof BAR_COLOURS)[number]

export interface BarProps {
  text: string // innerHTML (may carry <code>/version chips)
  startIdx: number // 0..9 day column the bar starts on
  endIdx: number // 0..9 day column the bar ends on (inclusive)
  colour: BarColour
  ms: boolean // milestone (single-moment box styling)
  hidden: boolean
}

const CHROME = ".li-chk,.tr-add-item,.tr-add-bar,.tr-x,.tr-assignees"
const MATRIX_COLS = 10 // day columns (grid columns 2..11)

export const MAX_ASSIGNEES = 2

/** ≤2-letter initials: first+last for a full name, else the first two letters. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  return name.trim().slice(0, 2).toUpperCase()
}

export function parseAssignees(li: Element): string[] {
  const raw = li.getAttribute("data-assignees") ?? ""
  return raw ? raw.split("|").filter(Boolean) : []
}

export function setAssignees(li: Element, names: string[]): void {
  const clean = names.filter(Boolean).slice(0, MAX_ASSIGNEES)
  if (clean.length) li.setAttribute("data-assignees", clean.join("|"))
  else li.removeAttribute("data-assignees")
}

/** Render each task's assignee initial-chips from its `data-assignees` (shown in
 *  view and edit). In edit mode, also append an assign/edit button. Rebuilt each
 *  call so it always reflects the current data. */
export function injectAssignees(content: HTMLElement, editing: boolean): void {
  content.querySelectorAll<HTMLElement>(".day li").forEach((li) => {
    li.querySelector(":scope > .tr-assignees")?.remove()
    const names = parseAssignees(li)
    if (!names.length && !editing) return
    const doc = li.ownerDocument
    const wrap = doc.createElement("span")
    wrap.className = "tr-assignees"
    wrap.contentEditable = "false"
    names.forEach((n) => {
      const chip = doc.createElement("span")
      chip.className = "asg-chip"
      chip.title = n
      chip.textContent = initials(n)
      wrap.appendChild(chip)
    })
    if (editing) {
      const btn = doc.createElement("button")
      btn.type = "button"
      btn.className = "tr-assign"
      btn.contentEditable = "false"
      btn.textContent = names.length ? "edit" : "+ assignee"
      wrap.appendChild(btn)
    }
    li.appendChild(wrap)
  })
}

/** Labels for the 10 matrix day columns, e.g. "Mon 17" — carry today's dates. */
export function dayColumns(content: ParentNode): string[] {
  return [...content.querySelectorAll(".matrix .mx-day")].map((d) => {
    const b = d.querySelector("b")?.textContent?.trim() ?? ""
    const s = d.querySelector("span")?.textContent?.trim() ?? ""
    return `${b} ${s}`.trim()
  })
}

export function parseBar(el: Element): BarProps {
  const style = el.getAttribute("style") ?? ""
  const m = /grid-column:\s*(\d+)\s*\/\s*(\d+)/.exec(style)
  const colStart = m ? Number(m[1]) : 2
  const colEnd = m ? Number(m[2]) : 4
  const colour = (BAR_COLOURS.find((c) => el.classList.contains(c)) ?? "grey") as BarColour
  return {
    text: el.innerHTML,
    startIdx: clampCol(colStart - 2),
    endIdx: clampCol(colEnd - 3),
    colour,
    ms: el.classList.contains("ms"),
    hidden: el.hasAttribute("data-hidden"),
  }
}

const clampCol = (n: number) => Math.max(0, Math.min(MATRIX_COLS - 1, n))

/** Write props back onto a bar element (grid-row is preserved). */
export function applyBar(el: Element, p: BarProps): void {
  const s = clampCol(p.startIdx)
  const e = Math.max(s, clampCol(p.endIdx))
  el.className = `bar ${p.colour}${p.ms ? " ms" : ""}`
  const style = el.getAttribute("style") ?? ""
  const gr = /grid-row:\s*([^;]+)/.exec(style)?.[1]?.trim() ?? "3"
  el.setAttribute("style", `grid-row: ${gr}; grid-column: ${2 + s} / ${3 + e};`)
  if (p.hidden) el.setAttribute("data-hidden", "")
  else el.removeAttribute("data-hidden")
  el.innerHTML = p.text
}

function laneRowEnd(laneEl: Element): number {
  const style = laneEl.getAttribute("style") ?? ""
  const span = /grid-row:\s*(-?\d+)\s*\/\s*(-?\d+)/.exec(style)
  if (span) return Number(span[2])
  const one = /grid-row:\s*(-?\d+)/.exec(style)
  return one ? Number(one[1]) + 1 : 4
}

/** Add a new bar at the bottom row of a lane, shifting every later row down by
 *  one so nothing overlaps (the matrix uses explicit grid-row placement). */
export function addBar(content: HTMLElement, laneEl: Element): Element {
  const matrix = content.querySelector(".matrix")
  if (!matrix) throw new Error("no matrix")
  const insertAt = laneRowEnd(laneEl)
  matrix.querySelectorAll<HTMLElement>('[style*="grid-row"]').forEach((el) => {
    const style = el.getAttribute("style") ?? ""
    el.setAttribute(
      "style",
      style.replace(
        /grid-row:\s*([^;]+)/,
        (_full, val: string) =>
          "grid-row: " +
          val.replace(/-?\d+/g, (num) => {
            const n = Number(num)
            return n >= insertAt ? String(n + 1) : num
          }),
      ),
    )
  })
  const bar = content.ownerDocument.createElement("div")
  bar.className = "bar grey"
  bar.setAttribute("style", `grid-row: ${insertAt}; grid-column: 2 / 4;`)
  bar.textContent = "New item"
  matrix.appendChild(bar)
  return bar
}

export function addItem(dayEl: Element): Element {
  const li = dayEl.ownerDocument.createElement("li")
  li.textContent = "New item"
  dayEl.querySelector("ul")?.appendChild(li)
  return li
}

/* ---------- per-week completion log ----------------------------------------
 * Completion is shared and keyed by the CALENDAR WEEK a task was ticked for, not
 * stored as a flag on the task, so a new week starts unchecked and the previous
 * week keeps showing what was actually completed:
 *   completions = { "<weekMonday ISO>": ["<taskKey>", …], … }
 * It lives in its own document field (not in the HTML) and each toggle is merged
 * server-side (POST /api/train/completion), so concurrent editors never clobber
 * one another. A taskKey is (dayIndex 0..4 · item index), identical for the same
 * recurring task across the Previous/Current/Next rows; the week disambiguates. */
export type CompletionLog = Record<string, string[]>

/** Day index 0..4 (Mon..Fri) from a day card's grid-column class (c2..c6). */
function dayIndex(day: Element): number {
  for (let c = 2; c <= 6; c++) if (day.classList.contains(`c${c}`)) return c - 2
  return 0
}

/** Stable key for a task, shared by the same recurring task in every week row. */
export function taskKey(li: Element): string {
  const day = li.closest(".day")
  if (!day) return "0.0"
  const idx = [...day.querySelectorAll("ul > li")].indexOf(li)
  return `${dayIndex(day)}.${idx}`
}

/** Which shown week a task's row is — 0=Previous, 1=Current, 2=Next (else -1). */
export function rowWeekIndex(li: Element): number {
  const day = li.closest(".day")
  if (day?.classList.contains("r2")) return 0
  if (day?.classList.contains("r3")) return 1
  if (day?.classList.contains("r4")) return 2
  return -1
}

/** The (week, task) a checkbox toggle applies to, or null if its row has no week. */
export function completionTarget(li: Element, mondays: string[]): { week: string; task: string } | null {
  const week = mondays[rowWeekIndex(li)]
  return week ? { week, task: taskKey(li) } : null
}

/** Completion checkboxes on every calendar item — present in view and edit (a
 *  shared, always-on task list). Each box reflects `log` for the calendar week its
 *  row currently represents (`mondays` = [prev, cur, next]). */
export function injectCheckboxes(content: HTMLElement, mondays: string[], log: CompletionLog): void {
  content.querySelectorAll<HTMLElement>(".day li").forEach((li) => {
    const week = mondays[rowWeekIndex(li)]
    const done = !!(week && log[week]?.includes(taskKey(li)))
    let cb = li.querySelector<HTMLInputElement>(":scope > .li-chk")
    if (!cb) {
      cb = li.ownerDocument.createElement("input")
      cb.type = "checkbox"
      cb.className = "li-chk"
      cb.contentEditable = "false" // non-editable island inside an editable <li>
      li.prepend(cb)
    }
    cb.checked = done
    li.classList.toggle("done", done)
  })
}

/** Edit-only chrome: add-item per day, remove-× per item, add-bar per lane. */
export function injectEditChrome(content: HTMLElement): void {
  content.querySelectorAll(".day").forEach((day) => {
    if (day.querySelector("ul") && !day.querySelector(":scope > .tr-add-item")) {
      const b = day.ownerDocument.createElement("button")
      b.type = "button"
      b.className = "tr-add-item"
      b.contentEditable = "false"
      b.textContent = "+ item"
      day.appendChild(b)
    }
    day.querySelectorAll(":scope ul > li").forEach((li) => {
      if (!li.querySelector(":scope > .tr-x")) {
        const x = li.ownerDocument.createElement("button")
        x.type = "button"
        x.className = "tr-x"
        x.contentEditable = "false"
        x.textContent = "×"
        li.appendChild(x)
      }
    })
  })
  content.querySelectorAll(".matrix .lane").forEach((lane) => {
    if (!lane.querySelector(":scope > .tr-add-bar")) {
      const b = lane.ownerDocument.createElement("button")
      b.type = "button"
      b.className = "tr-add-bar"
      b.contentEditable = "false"
      b.textContent = "+ bar"
      lane.appendChild(b)
    }
  })
}

export function removeEditChrome(content: HTMLElement): void {
  content.querySelectorAll(".tr-add-item,.tr-add-bar,.tr-x").forEach((el) => el.remove())
}

/** Propagate the current week's tasks forward into the recurring template: copy
 *  each Current (row 3) day-card task list onto the matching Next (row 4) day
 *  (same weekday), so future renders (which reuse this template) match. Task text
 *  and assignees carry over; completion is reset; version placeholders are shifted
 *  one cycle (v{k} → v{k+1}) so the numbers stay correct for the later week.
 *  (The Previous week, row 2, is historical and left untouched.)
 *
 *  Operates on serialized (chrome-free) HTML and returns HTML. */
export function propagateWeekToFuture(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html")
  const cal = doc.querySelector(".cal")
  if (!cal) return html
  const current = [...cal.querySelectorAll(".day.r3")] // current week (Mon…Fri)
  const next = [...cal.querySelectorAll(".day.r4")] // next week / template
  const n = Math.min(current.length, next.length)
  for (let i = 0; i < n; i++) {
    const src = current[i].querySelector("ul")
    const dst = next[i].querySelector("ul")
    if (!src || !dst) continue
    const copy = src.cloneNode(true) as HTMLElement
    copy.querySelectorAll("[data-ph]").forEach((el) => {
      const m = /^v(\d+)\.(.+)$/.exec(el.getAttribute("data-ph") ?? "")
      if (m) el.setAttribute("data-ph", `v${Number(m[1]) + 1}.${m[2]}`)
    })
    copy.querySelectorAll("li[data-done]").forEach((li) => li.removeAttribute("data-done"))
    dst.replaceWith(copy)
  }
  const sheet = doc.querySelector(".sheet")
  return sheet ? sheet.outerHTML : html
}

/** Clone → drop all chrome + contenteditable attrs → sanitized .sheet HTML. */
export function serialize(content: HTMLElement, sanitize: (h: string) => string): string {
  const clone = content.cloneNode(true) as HTMLElement
  clone.querySelectorAll(CHROME).forEach((el) => el.remove())
  clone.querySelectorAll("[contenteditable]").forEach((el) => el.removeAttribute("contenteditable"))
  const sheet = clone.querySelector(".sheet")
  return sanitize(sheet ? sheet.outerHTML : clone.innerHTML)
}
